import type { ComponentProps } from "react";
import { SelectOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillOut } from "../../../../../api/bills";
import type { TableState } from "../../../../reservations/logic/tableStatus";
import { OrderDetails } from "../OrderDetails";
import { tableReservationNoticeText } from "../manage/tableReservationNotice";
import { customerDiscountOf } from "../../billDiscount";
import { TablesEmptyState } from "../TablesEmptyState";
import { SchemaTableHeader } from "./SchemaTableHeader";
import type { SchemaTableRef } from "./orderingFloorData";
import { SIDE_PANEL_WIDTH_CLASS } from "../../sidePanelWidth";

type DetailsProps = Omit<ComponentProps<typeof OrderDetails>, "bill" | "layout">;

/**
 * Панелът вдясно от схемата — ЦЯЛАТА информация за избраната маса:
 * - заглавието (`SchemaTableHeader`) — всичко от картата на масата в списъка:
 *   сервитьор, места, гости, „Карта — без бон", резервацията (±% на клиента е
 *   горе вдясно на масата в схемата и в лентата на клиента);
 * - поръчаното (`OrderDetails` в тесния си вид): добавки, модификатори,
 *   коментари, отстъпки/надбавки, клиент, настанената резервация,
 *   „Обобщено", сервиране, повторна поръчка;
 * - „Общо" на сметката най-отдолу, на същия ред — спестеното/надбавката от
 *   закачения клиент (в списъка е в лентата на клиента);
 * - маса без сметка: свободна — подсказка как се отваря; заета от колега —
 *   „Маса 2 е заета от Мария"; заета от настанена резервация без видима
 *   сметка — „Маса 2 е заета от резервацията на Иванов (настанени 19:05)";
 *   нищо избрано — празното състояние.
 */
export const SchemaTablePanel = ({
  table,
  tableState,
  takenBy,
  seats,
  bills,
  selectedBill,
  selectedBillTableNames,
  onSelectBill,
  details,
}: {
  /** Избраната маса от схемата; null — временна маса или нищо. */
  table: SchemaTableRef | null;
  tableState: TableState | null;
  /** Чужда сметка на масата, която списъкът не вижда; null — няма. */
  takenBy: { waiterName: string | null } | null;
  /** Местата на масата от схемата (и на нефискализирана с името ѝ); null — няма. */
  seats: number | null;
  /** Сметките на масата (изборът между тях при няколко). */
  bills: BillOut[];
  selectedBill: BillOut | null;
  /** Масите, които избраната сметка държи сама (при „Една сметка" за
   *  резервация на няколко маси — всичките ѝ). */
  selectedBillTableNames: string[];
  onSelectBill: (billId: number) => void;
  details: DetailsProps;
}) => {
  const { t } = useTranslation();
  const tableName = table?.table.name ?? "";
  const discount = selectedBill ? customerDiscountOf(selectedBill) : null;
  const savings = discount && discount.percent !== 0 ? discount : null;

  // Маса от схемата без сметка: какво я държи (иначе е свободна).
  const seatedReservation =
    tableState?.kind === "occupied" ? tableState.reservation : null;
  const heldBy = takenBy
    ? takenBy.waiterName
      ? t("seatReservation.takenBy", { table: tableName, waiter: takenBy.waiterName })
      : t("seatReservation.takenByOther", { table: tableName })
    : seatedReservation
      ? tableReservationNoticeText(tableName, { ...seatedReservation, status: "seated" }, t)
      : null;
  const tableWithoutBill = heldBy ? (
    <div className="rounded-[14px] border border-pos-ctl bg-pos-card px-5 py-4 text-center text-[13px] font-semibold leading-[1.5] text-pos-ink">
      {heldBy}
    </div>
  ) : tableState?.kind === "free" ? (
    <div className="rounded-[14px] border border-dashed border-pos-ctl px-5 py-6 text-center text-[13px] font-medium leading-[1.5] text-pos-soft">
      {t("ordering.schema.freeTableHint")}
    </div>
  ) : null;

  return (
    // Ширината е като на „Преглед на поръчката" в менюто — 1:1.
    <aside className={`flex ${SIDE_PANEL_WIDTH_CLASS} shrink-0 flex-col min-h-0 border-l border-pos-line bg-pos-panel`}>
      {(table || selectedBill) && (
        <SchemaTableHeader
          table={table}
          tableState={tableState}
          takenByWaiterName={takenBy?.waiterName ?? null}
          seats={seats}
          bill={selectedBill}
          billTableNames={selectedBillTableNames}
          tableBills={table ? bills : []}
          onSelectBill={onSelectBill}
        />
      )}

      <div className="min-h-0 flex-1 bg-pos-canvas">
        {selectedBill ? (
          <OrderDetails bill={selectedBill} layout="panel" {...details} />
        ) : table ? (
          tableWithoutBill && <div className="p-4">{tableWithoutBill}</div>
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <TablesEmptyState
              icon={<SelectOutlined />}
              title={t("ordering.noTableSelected")}
              description={t("ordering.schema.noTableSelectedHint")}
            />
          </div>
        )}
      </div>

      {/* „Общо" на сметката — най-отдолу, над лентата с бутоните */}
      {selectedBill && (
        <div className="flex shrink-0 items-baseline justify-between gap-3 border-t border-pos-line2 bg-pos-panel px-5 py-3.5">
          <span className="flex min-w-0 items-baseline gap-3">
            <span className="shrink-0 text-[14px] font-semibold text-pos-soft2">{t("common.total")}</span>
            {savings && (
              <span className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
                <span className="text-[11px] font-medium text-pos-soft2">
                  {t(savings.isSurcharge ? "clients.bannerSurcharged" : "clients.bannerSaved")}
                </span>
                <span className="font-pos-mono text-sm font-bold text-pos-accent-ink [font-variant-numeric:tabular-nums]">
                  {savings.amount.toFixed(2)}
                </span>
                <span className="text-[10px] font-medium text-pos-muted">EUR</span>
              </span>
            )}
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className="font-pos-mono text-[24px] font-bold leading-none text-pos-ink">
              {parseFloat(selectedBill.total_amount).toFixed(2)}
            </span>
            <span className="font-pos-mono text-[11px] font-semibold text-pos-muted">EUR</span>
          </span>
        </div>
      )}
    </aside>
  );
};
